import { createTheme, responsiveFontSizes, type Theme } from '@mui/material';

// Sentry Solutions Brand Palette (defaults).
// Ink #0B1320, Hi-Vis #FF5B1F, Signal Teal #00C2A8, Amber #F2A900, Paper #F5F2EC
// Typography: Inter (body), Archivo (display fallback), JetBrains Mono (data).
//
// SS-DEC-005: brand colors are overridable per-organization (enterprise
// `custom_branding`). `buildTheme(mode, branding)` merges the org's stored
// overrides over these defaults; an absent value falls back to the default.

export interface BrandingOverrides {
  primary?: string;
  secondary?: string;
  backgroundDefault?: string;
  backgroundPaper?: string;
  error?: string;
  warning?: string;
  success?: string;
}

const DARK_DEFAULTS = {
  primary: '#FF5B1F',
  secondary: '#00C2A8',
  backgroundDefault: '#0B1320',
  backgroundPaper: '#1a1a2e',
  error: '#f44336',
  warning: '#F2A900',
  success: '#4caf50',
} as const;

const LIGHT_DEFAULTS = {
  primary: '#FF5B1F',
  secondary: '#00C2A8',
  backgroundDefault: '#F5F2EC',
  backgroundPaper: '#ffffff',
  error: '#f44336',
  warning: '#F2A900',
  success: '#4caf50',
} as const;

const TYPOGRAPHY = {
  fontFamily: '"Inter", "Archivo", "Roboto", sans-serif',
  button: { textTransform: 'none' as const },
};

const darkComponents = {
  MuiButton: { styleOverrides: { root: { borderRadius: 8, padding: '8px 16px' } } },
  MuiCard: { styleOverrides: { root: { borderRadius: 12 } } },
  MuiPaper: { styleOverrides: { root: { borderRadius: 12 } } },
  MuiChip: { styleOverrides: { root: { borderRadius: 6 } } },
  MuiTableContainer: { styleOverrides: { root: { overflowX: 'auto' as const } } },
  MuiDialog: {
    styleOverrides: {
      paper: { margin: 16, maxHeight: 'calc(100% - 32px)', width: 'calc(100% - 32px)' },
    },
  },
};

const lightComponents = {
  MuiButton: { styleOverrides: { root: { borderRadius: 8, padding: '8px 16px' } } },
  MuiCard: { styleOverrides: { root: { borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' } } },
  MuiTableContainer: { styleOverrides: { root: { overflowX: 'auto' as const } } },
  MuiDialog: {
    styleOverrides: {
      paper: { margin: 16, maxHeight: 'calc(100% - 32px)', width: 'calc(100% - 32px)' },
    },
  },
};

const pick = (override: string | undefined, fallback: string) =>
  typeof override === 'string' && /^#[0-9a-fA-F]{6}$/.test(override) ? override : fallback;

/**
 * Build a theme for the given mode, optionally overlaying per-org brand colors.
 * Invalid/absent overrides fall back to the Sentry Solutions defaults, so a bad
 * value can never produce an unreadable theme.
 */
export function buildTheme(mode: 'dark' | 'light', branding?: BrandingOverrides | null): Theme {
  const isDark = mode === 'dark';
  const d = isDark ? DARK_DEFAULTS : LIGHT_DEFAULTS;
  const b = branding || {};
  return responsiveFontSizes(
    createTheme({
      palette: {
        mode,
        primary: { main: pick(b.primary, d.primary) },
        secondary: { main: pick(b.secondary, d.secondary) },
        background: {
          default: pick(b.backgroundDefault, d.backgroundDefault),
          paper: pick(b.backgroundPaper, d.backgroundPaper),
        },
        error: { main: pick(b.error, d.error) },
        warning: { main: pick(b.warning, d.warning) },
        success: { main: pick(b.success, d.success) },
      },
      typography: TYPOGRAPHY,
      shape: { borderRadius: 8 },
      components: isDark ? darkComponents : lightComponents,
    }),
  );
}

export const darkTheme = buildTheme('dark');
export const lightTheme = buildTheme('light');
